import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useDataSource } from "@/data/DataProvider";
import { useIdentity } from "@/auth/ClientAuthProvider";
import type { ThreadMessage } from "@/data/types";

export type { ThreadMessage };

export function threadKey(orgId: string, leadId: string) {
  return ["thread", orgId, leadId] as const;
}

/**
 * The lead's SMS + app conversation. The source pushes a change signal
 * (realtime in production); a 20s poll covers the case where it is quiet.
 */
export function useThread() {
  const source = useDataSource();
  const id = useIdentity();
  const queryClient = useQueryClient();
  const key = threadKey(id.orgId, id.leadId);

  const query = useQuery({
    queryKey: key,
    queryFn: () => source.getThread(id),
    refetchInterval: 20_000,
    staleTime: 5_000,
  });

  useEffect(() => {
    return source.subscribeThread(id, () => {
      void queryClient.invalidateQueries({ queryKey: key });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, id.leadId, id.orgId]);

  return query;
}

export function useSendMessage() {
  const source = useDataSource();
  const id = useIdentity();
  const queryClient = useQueryClient();
  const key = threadKey(id.orgId, id.leadId);

  return useMutation({
    mutationFn: (content: string) => source.sendMessage(id, content),
    onMutate: async (content) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<ThreadMessage[]>(key) ?? [];
      const optimistic: ThreadMessage = {
        id: `local-${Date.now()}`,
        content,
        direction: "inbound",
        message_type: "app",
        created_at: new Date().toISOString(),
        status: "sending",
        pending: true,
      };
      queryClient.setQueryData<ThreadMessage[]>(key, [...previous, optimistic]);
      return { previous };
    },
    onError: (_err, _content, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(key, ctx.previous);
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: key }),
  });
}
